import { FunctionComponent, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Person, Requirement } from '../../types';
import {
  Button,
  Section,
  SectionHeading,
  Confirmation,
  PlatformFeeInformation,
} from '../../components';
import { successCallbackPath } from '../../utils/paths';
import { fetchApi } from '../../utils/fetchApi';
import { ApiStatus, IApiData } from '../../utils/types';
import { Renderer, formSpecToRendererConfig } from '../../components/forms';
import DefaultAnnualReportSpec from '../../components/forms/specs/annual_report_spec.json';
import { useNavigate } from 'react-router-dom';
import { FormError } from '../../components/forms/FormError';
import { useRequirementFee } from '../../hooks/useRequirementFee';
import { RequirementFeeText } from '../../components/RequirementFeeText';

export const AnnualReportReqsByState: Record<string, string[]> = {
  DE: ['6290dd69', '0a47206c'],
};

type DefaultAnnualReportProps = {
  locationId: string;
  requirement: Requirement;
  peopleData: Person[];
  callbackUrl?: string | null;
};

type AnnualReportIFormValue = {
  total_authorized_shares: number;
  total_issued_shares?: number;
  gross_assets?: number;
};

export const DefaultAnnualReport: FunctionComponent<
  DefaultAnnualReportProps
> = ({ locationId: rawLocationId, requirement, peopleData, callbackUrl }) => {
  const fee = useRequirementFee(requirement);
  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);
  const [isSubmitSuccessful, setSubmitSuccessful] = useState<boolean>(false);
  const locationId = rawLocationId.toLowerCase();

  const navigate = useNavigate();

  const formMethods = useForm<AnnualReportIFormValue>();
  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit = (data: AnnualReportIFormValue) => {
    const {
      total_authorized_shares: totalAuthorizedShares,
      total_issued_shares: totalIssuedShares,
      gross_assets: grossAssets,
    } = data;

    setHasSubmitError(false);

    const payload = {
      legal_entity_requirement_id: requirement.id,
      authorized_shares: totalAuthorizedShares,
      ...(totalIssuedShares && { issued_shares: totalIssuedShares }),
      ...(grossAssets && { gross_assets: grossAssets }),
    };

    fetchApi({
      url: `/api/legal_entity/locations/${locationId}/annual_report`,
      method: 'POST',
      body: payload,
    }).then(({ status }: IApiData) => {
      if (status === ApiStatus.Error) {
        setHasSubmitError(true);
      } else {
        if (callbackUrl) {
          navigate(successCallbackPath(callbackUrl), { replace: true });
          return;
        }

        setSubmitSuccessful(true);
      }
    });
  };

  const loadedSpec = DefaultAnnualReportSpec;

  return (
    <Section className="flex flex-col items-center">
      {isSubmitSuccessful ? (
        <Confirmation
          heading="Success"
          description="Your request is on its way! We'll send you an email once we begin processing."
        />
      ) : (
        <>
          <SectionHeading
            className="w-96 pb-4"
            text={requirement.title || loadedSpec.title}
          />
          <FormProvider {...formMethods}>
            <form className="my-8 w-96" onSubmit={handleSubmit(onSubmit)}>
              <Renderer
                config={formSpecToRendererConfig(loadedSpec)}
                errors={errors}
                peopleData={peopleData}
              />
              <p className="mt-8 flex flex-col">
                <Button type="submit" size="large">
                  Submit
                </Button>
                <PlatformFeeInformation
                  requirement={requirement}
                  regionCode={requirement.region[0]}
                  domesticFee={requirement.domestic_fee}
                  foreignFee={requirement.foreign_fee}
                  baseFee={100}
                  displayRegisteredAgentFee={false}
                />
                {hasSubmitError && <FormError />}
                {fee && (
                  <div className="mt-4">
                    <RequirementFeeText fee={fee} />
                  </div>
                )}
              </p>
            </form>
          </FormProvider>
        </>
      )}
    </Section>
  );
};
